import type { ApiResult } from '../types';
import type { WizardFlowKind } from '../utils/wizardCategoryRoute';
import { CIVIL_ADVISE_FIELD_LABELS } from '../pages/wizard/civilAdvise/civilAdviseFieldLabels';
import { getCivilAdviseRequestViewData } from './civilAdviseCaseRequests';
import {
  getCaseRequestDocuments,
  type CriminalDocumentsView,
  type CriminalSubmittedDocumentView,
} from './criminalCaseRequests';

const PREVIOUS_ADVICE_DOC_TYPE = CIVIL_ADVISE_FIELD_LABELS.previousAdviceAttachment;

function mergePreviousAdviceIntoSubmitted(
  documents: CriminalDocumentsView,
  previousAdvice: CriminalSubmittedDocumentView[],
): CriminalDocumentsView {
  if (!previousAdvice.length) return documents;

  const existingKeys = new Set(
    documents.submitted
      .map((d) => d.fileName.trim())
      .filter(Boolean),
  );

  const extras = previousAdvice.filter((d) => {
    const key = d.fileName.trim();
    return key ? !existingKeys.has(key) : true;
  });

  if (!extras.length) return documents;
  return {
    ...documents,
    submitted: [...extras, ...documents.submitted],
  };
}

async function civilAdvisePreviousAdviceAsSubmitted(
  caseRequestId: string,
): Promise<CriminalSubmittedDocumentView[]> {
  const view = await getCivilAdviseRequestViewData(caseRequestId);
  if (!view.success || !view.data) return [];

  return view.data.draft.previousAdviceFiles
    .map((f, index): CriminalSubmittedDocumentView | null => {
      const fileName = (f.fileName ?? '').trim();
      const originalFileName = (f.originalFileName ?? '').trim() || null;
      if (!fileName && !originalFileName) return null;
      return {
        id: f.id?.trim() || `previous-advice-${index}`,
        documentTypeId: '',
        documentTypeName: PREVIOUS_ADVICE_DOC_TYPE,
        status: 'Uploaded',
        reason: '',
        fileName: fileName || (originalFileName ?? ''),
        originalFileName,
        uploadedAt: f.uploadTime ?? '',
      };
    })
    .filter((d): d is CriminalSubmittedDocumentView => d !== null);
}

/**
 * CMS-CRC-7 plus category-specific intake attachments that are not returned by the
 * shared documents API (e.g. Civil Advise previous-advice files on step 1).
 */
export async function getCaseRequestDocumentsEnriched(
  caseRequestId: string,
  flow?: WizardFlowKind,
): Promise<ApiResult<CriminalDocumentsView>> {
  const base = await getCaseRequestDocuments(caseRequestId);
  if (!base.success) return base;

  if (flow === 'civil-advise') {
    const previousAdvice = await civilAdvisePreviousAdviceAsSubmitted(caseRequestId);
    return {
      success: true,
      data: mergePreviousAdviceIntoSubmitted(base.data, previousAdvice),
    };
  }

  return base;
}
